import React, { useEffect, useMemo, useRef, useState } from 'react';
import CanvasJSReact from '@canvasjs/react-charts';
import { buildChartSeries, liveVisiblePoints, medianInterval } from './gatewaySamples';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

// The live chart adapts to the sample cadence (there is no fixed sample rate), following
// streaming-chart best practice (cf. TradingView Lightweight Charts: discrete updates;
// amCharts live-data: continuous scroll for fast streams):
//
//  - Fast data (interval <= CONTINUOUS_MAX_INTERVAL_MS): pan the viewport toward "now"
//    every frame for a smooth continuous glide. No dot markers (sub-pixel dots shimmer).
//  - Slow data (interval > threshold): anchor to the newest sample and only animate a short
//    "swipe" when a new point arrives, static in between. Dots shown so points are visible.
//
// Either way the window's time span holds ~LIVE_VISIBLE_POINTS samples, so the chart shows
// the same number of points whether data arrives every second or every minute.
const CONTINUOUS_MAX_INTERVAL_MS = 30 * 1000;
// Floor just guards against an absurdly tiny window at sub-second rates; it must stay below
// the LIVE_VISIBLE_POINTS span at fast rates, or it would over-widen the window and leave
// empty space on the left until enough points accumulate.
const MIN_WINDOW_MS = 3 * 1000;
const MAX_WINDOW_MS = 6 * 60 * 60 * 1000;
// Empty "lead" space kept ahead of the newest point (~18% of the window), so the latest
// reading sits around 82% across — well to the right, but not jammed against the edge.
const LEAD_FRAC = 0.18;

const GatewayLiveData = ({ samples }) => {
    const [view, setView] = useState('graph');

    const chartRef = useRef(null);
    const samplesRef = useRef(samples);
    const seriesRef = useRef(buildChartSeries([], 4));
    const versionRef = useRef(0);
    const appliedVersionRef = useRef(-1);
    const visibilityRef = useRef({});
    const rafRef = useRef(null);
    const windowMsRef = useRef(null);
    const targetWindowRef = useRef(MIN_WINDOW_MS);
    const continuousRef = useRef(true);
    const latestTimeRef = useRef(null);
    const currentMaxRef = useRef(null);

    samplesRef.current = samples;
    const latest = samples.length ? samples[samples.length - 1] : null;

    // Stable options object — identity never changes, so the React wrapper never tears
    // down/rebuilds the chart; we mutate + render() it ourselves in the rAF loop below.
    const options = useMemo(
        () => ({
            height: 320,
            animationEnabled: false,
            axisX: {
                valueFormatString: 'HH:mm:ss',
                labelAngle: -25,
                labelAutoFit: false,
            },
            axisY: {
                lineThickness: 1,
                stripLines: [{ value: 0, color: '#aab1bb', thickness: 1 }],
            },
            legend: {
                cursor: 'pointer',
                itemclick: e => {
                    e.dataSeries.visible = !(typeof e.dataSeries.visible === 'undefined' || e.dataSeries.visible);
                    visibilityRef.current[e.dataSeries.name] = e.dataSeries.visible;
                    e.chart.render();
                },
            },
            toolTip: { shared: true },
            data: seriesRef.current,
        }),
        []
    );

    // Rebuild the series only when samples actually change (not every frame).
    useEffect(() => {
        // Buffer cleared (e.g. device switch) — reset eased state so the view re-fits.
        if (samplesRef.current.length === 0) {
            windowMsRef.current = null;
            currentMaxRef.current = null;
            latestTimeRef.current = null;
        }

        // Detect cadence → pick mode. Round the interval (timestamps drift a few ms) so the
        // window width is stable; only adopt a new width when it changes meaningfully (>15%).
        const interval = Math.round((medianInterval(samplesRef.current) || 1000) / 1000) * 1000;
        const continuous = interval <= CONTINUOUS_MAX_INTERVAL_MS;
        continuousRef.current = continuous;

        // Always show dots at the data points (in both continuous and discrete modes).
        const series = buildChartSeries(samplesRef.current, 4);
        series.forEach(s => {
            if (visibilityRef.current[s.name] !== undefined) {
                s.visible = visibilityRef.current[s.name];
            }
        });
        seriesRef.current = series;
        versionRef.current += 1;

        // Window spans an adaptive number of samples (N points cover N-1 intervals).
        const dataArea = interval * Math.max(liveVisiblePoints(interval) - 1, 1);
        const next = Math.min(Math.max(dataArea / (1 - LEAD_FRAC), MIN_WINDOW_MS), MAX_WINDOW_MS);
        const cur = targetWindowRef.current;
        if (!cur || Math.abs(next - cur) / cur > 0.15) {
            targetWindowRef.current = next;
        }

        // Anchor for discrete mode: the newest sample's time.
        const newest = samplesRef.current[samplesRef.current.length - 1];
        const t = newest ? new Date(newest.timestamp ?? newest.time).getTime() : null;
        if (t && !Number.isNaN(t)) {
            latestTimeRef.current = t;
        }
    }, [samples]);

    // Animation loop. Continuous mode pans toward "now" and renders every frame (smooth
    // glide); discrete mode anchors to the newest sample and only renders while easing into
    // a new point (a short "swipe"), staying completely static — no redraws — in between.
    useEffect(() => {
        if (view !== 'graph') {
            return undefined;
        }
        let mounted = true;

        const tick = () => {
            if (!mounted) {
                return;
            }
            const chart = chartRef.current;
            if (chart) {
                const dataChanged = appliedVersionRef.current !== versionRef.current;
                if (dataChanged) {
                    chart.options.data = seriesRef.current;
                    appliedVersionRef.current = versionRef.current;
                }

                // Ease the window width toward target, snapping once close (stable width).
                const targetW = targetWindowRef.current;
                if (windowMsRef.current == null || Math.abs(targetW - windowMsRef.current) < targetW * 0.01) {
                    windowMsRef.current = targetW;
                } else {
                    windowMsRef.current += (targetW - windowMsRef.current) * 0.08;
                }
                const w = windowMsRef.current;

                if (continuousRef.current) {
                    // Continuous: right edge tracks wall-clock now; render every frame.
                    const max = Date.now() + w * LEAD_FRAC;
                    chart.options.axisX.viewportMaximum = max;
                    chart.options.axisX.viewportMinimum = max - w;
                    chart.render();
                } else if (latestTimeRef.current != null) {
                    // Discrete: right edge eases toward (newest sample + lead). It moves only
                    // when a new sample arrives, then settles — render only while moving.
                    const targetMax = latestTimeRef.current + w * LEAD_FRAC;
                    if (currentMaxRef.current == null) {
                        currentMaxRef.current = targetMax;
                    }
                    const moving = Math.abs(targetMax - currentMaxRef.current) > w * 0.002;
                    if (moving) {
                        currentMaxRef.current += (targetMax - currentMaxRef.current) * 0.18;
                    } else {
                        currentMaxRef.current = targetMax;
                    }
                    if (dataChanged || moving) {
                        const max = currentMaxRef.current;
                        chart.options.axisX.viewportMaximum = max;
                        chart.options.axisX.viewportMinimum = max - w;
                        chart.render();
                    }
                }
            }
            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => {
            mounted = false;
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [view]);

    return (
        <div className="gw-live">
            <div className="gw-live__head">
                <div className="gw-live__toggle">
                    <button
                        type="button"
                        className={`gw-live__toggle-btn ${view === 'graph' ? 'gw-live__toggle-btn--active' : ''}`}
                        onClick={() => setView('graph')}
                    >
                        <i className="fa-solid fa-chart-line" /> Graph
                    </button>
                    <button
                        type="button"
                        className={`gw-live__toggle-btn ${view === 'json' ? 'gw-live__toggle-btn--active' : ''}`}
                        onClick={() => setView('json')}
                    >
                        <i className="fa-solid fa-code" /> JSON
                    </button>
                </div>
            </div>

            {view === 'graph' ? (
                samples.length ? (
                    <CanvasJSChart options={options} onRef={ref => (chartRef.current = ref)} />
                ) : (
                    <p className="gw-empty">Waiting for live samples…</p>
                )
            ) : (
                <pre className="gw-live__json">{latest ? JSON.stringify(latest, null, 2) : 'Waiting for live sample…'}</pre>
            )}
        </div>
    );
};

export default GatewayLiveData;
